import { useUploadGuard } from '../hooks/useUploadGuard';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { appendWorkflowName, StageBackButton, useFlow } from '../components/StageWrapper';
import {
  fillComplexServicesForm9,
  getComplexServicesForm9,
  saveComplexServicesForm9,
  type ComplexServicesArtifactStageData,
} from '../api/complexServicesFunctions';
import { toUserFacingError } from '../utils/userFacingError';
import { BadgeSlot, ComplexField } from './complexServicesFields';
import SimpleStageTour, { type SimpleStageTourHandle } from '../components/tour/SimpleStageTour';
import { getSteps } from './ComplexServicesForm9.tour';
import { SavedWorkActionRow } from '../components/SavedWorkActionRow';
import { StageTourButton } from '../components/StageTourButton';

/** Complex Services stage 619 — AF Form 9 / Purchase Request. */
export interface ComplexServicesForm9Props {
  flowId?: string | null;
  initialData?: unknown;
  onComplete: (data?: unknown) => void;
}

interface LineItem {
  item_number: string;
  description: string;
  quantity: string;
  unit: string;
  estimated_unit_price: string;
}

interface Form9Form {
  purchase_request_no: string;
  requirement_title: string;
  purpose: string;
  requester_name: string;
  requesting_organization: string;
  total_amount: string;
  funding_document_number: string;
  accounting_classification: string;
  certifier_name: string;
  certifier_organization: string;
  certifier_date: string;
  approver_name: string;
  approval_date: string;
}

interface ArtifactRef {
  artifact_id?: string;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  source?: string;
}

interface Form9Data extends Partial<Form9Form> {
  line_items?: Partial<LineItem>[];
  artifact_ref?: ArtifactRef;
  is_complete?: boolean;
  completion_errors?: string[];
  meta_auto_filled_fields?: string[];
  meta_user_edited_fields?: string[];
}

const EMPTY_FORM: Form9Form = {
  purchase_request_no: '',
  requirement_title: '',
  purpose: '',
  requester_name: '',
  requesting_organization: '',
  total_amount: '',
  funding_document_number: '',
  accounting_classification: '',
  certifier_name: '',
  certifier_organization: '',
  certifier_date: '',
  approver_name: '',
  approval_date: '',
};

const EMPTY_LINE_ITEM: LineItem = {
  item_number: '',
  description: '',
  quantity: '',
  unit: '',
  estimated_unit_price: '',
};

const ACCEPTED_FILE_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

function normaliseLineItems(data: unknown): LineItem[] {
  if (!Array.isArray(data) || data.length === 0) return [{ ...EMPTY_LINE_ITEM }];
  return data.map((row) => ({ ...EMPTY_LINE_ITEM, ...(row as Partial<LineItem>) }));
}

function asForm9Data(data: unknown): Form9Data {
  return data && typeof data === 'object' ? (data as Form9Data) : {};
}

function normaliseForm(data: Form9Data, fallback: Form9Form = EMPTY_FORM): Form9Form {
  const form = { ...EMPTY_FORM };
  (Object.keys(EMPTY_FORM) as Array<keyof Form9Form>).forEach((key) => {
    form[key] = String(data[key] ?? fallback[key] ?? '');
  });
  return form;
}

function artifactFrom(data: Form9Data): ArtifactRef | null {
  return data.artifact_ref && typeof data.artifact_ref === 'object' ? data.artifact_ref : null;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the Form 9 artifact.'));
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      let binary = '';
      const chunkSize = 0x8000;
      for (let index = 0; index < bytes.length; index += chunkSize) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(index, index + chunkSize)));
      }
      resolve(btoa(binary));
    };
    reader.readAsArrayBuffer(file);
  });
}

const ComplexServicesForm9: React.FC<ComplexServicesForm9Props> = ({
  flowId,
  initialData,
  onComplete,
}) => {
  const tourRef = useRef<SimpleStageTourHandle>(null);
  const { flowName, isViewOnly, isSampleFlow, onSampleContinue } = useFlow();
  const { disabledRef: readOnlyRef, openPicker, blockEvent } = useUploadGuard(isViewOnly);
  const seed = useMemo(() => asForm9Data(initialData), [initialData]);
  const [form, setForm] = useState<Form9Form>(() => normaliseForm(seed));
  const [lineItems, setLineItems] = useState<LineItem[]>(() => normaliseLineItems(seed.line_items));
  const [artifact, setArtifact] = useState<ArtifactRef | null>(() => artifactFrom(seed));
  const [serverComplete, setServerComplete] = useState(() => seed.is_complete === true);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFilling, setIsFilling] = useState(false);
  const [useSavedWork, setUseSavedWork] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoFilled, setAutoFilled] = useState<Set<string>>(
    () => new Set(seed.meta_auto_filled_fields ?? []),
  );
  const [userEdited, setUserEdited] = useState<Set<string>>(
    () => new Set(seed.meta_user_edited_fields ?? []),
  );
  const autoFilledRef = useRef(autoFilled);
  const userEditedRef = useRef(userEdited);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSavedRef = useRef<string | null>(null);
  const hasLocalEditsRef = useRef(false);

  const applyServerData = useCallback((value: unknown) => {
    const data = asForm9Data(value);
    const protectedFields = new Set([
      ...Array.from(userEditedRef.current),
      ...(data.meta_user_edited_fields ?? []),
    ]);
    setForm((previous) => {
      const next = normaliseForm(data, previous);
      (Object.keys(EMPTY_FORM) as Array<keyof Form9Form>).forEach((field) => {
        if (protectedFields.has(field)) next[field] = previous[field];
      });
      return next;
    });
    if (Array.isArray(data.line_items) && !protectedFields.has('line_items')) {
      setLineItems(normaliseLineItems(data.line_items));
    }
    setArtifact(artifactFrom(data));
    setServerComplete(data.is_complete === true);
    userEditedRef.current = protectedFields;
    setUserEdited(protectedFields);
    const nextAutoFilled = new Set(
      [...Array.from(autoFilledRef.current), ...(data.meta_auto_filled_fields ?? [])]
        .filter((field) => !protectedFields.has(field)),
    );
    autoFilledRef.current = nextAutoFilled;
    setAutoFilled(nextAutoFilled);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!flowId) return undefined;
    void getComplexServicesForm9(flowId)
      .then((data) => {
        if (cancelled || !data || hasLocalEditsRef.current) return;
        applyServerData(data);
        lastSavedRef.current = JSON.stringify(data);
      })
      .catch(() => {
        /* Loading a draft must not prevent the user from creating a new one. */
      });
    return () => {
      cancelled = true;
    };
  }, [applyServerData, flowId]);

  const buildPayload = useCallback((): ComplexServicesArtifactStageData => ({
    ...form,
    line_items: lineItems,
    meta_auto_filled_fields: Array.from(autoFilled),
    meta_user_edited_fields: Array.from(userEdited),
  }), [autoFilled, form, lineItems, userEdited]);

  const localFormComplete = useMemo(() => {
    const allTextPresent = Object.values(form).every((value) => String(value).trim().length > 0);
    const hasLineItem = lineItems.some((item) => item.description.trim().length > 0);
    const amount = Number(String(form.total_amount).replace(/[$,]/g, ''));
    return allTextPresent && hasLineItem && Number.isFinite(amount) && amount > 0;
  }, [form, lineItems]);

  const canContinue = localFormComplete && serverComplete && artifact !== null && !saving && !isFilling && !isViewOnly;

  const autoSave = useCallback(() => {
    if (!flowId || isViewOnly || saving) return;
    const payload = buildPayload();
    const snapshot = JSON.stringify(payload);
    if (snapshot === lastSavedRef.current) return;
    lastSavedRef.current = snapshot;
    void saveComplexServicesForm9(flowId, payload).then(applyServerData).catch(() => {
      // Resetting the snapshot lets a later blur retry a transient failure.
      lastSavedRef.current = null;
    });
  }, [applyServerData, buildPayload, flowId, isViewOnly, saving]);

  useEffect(() => {
    lastSavedRef.current = JSON.stringify(buildPayload());
    // The baseline belongs to the first render; subsequent edits are saved on blur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field: keyof Form9Form, value: string) => {
    hasLocalEditsRef.current = true;
    if (!userEditedRef.current.has(field)) {
      const next = new Set(userEditedRef.current).add(field);
      userEditedRef.current = next;
      setUserEdited(next);
    }
    setForm((previous) => ({ ...previous, [field]: value }));
    // Server validation must run again after the next save; a stale positive
    // projection must never be treated as proof after the form is edited.
    setServerComplete(false);
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string) => {
    hasLocalEditsRef.current = true;
    if (!userEditedRef.current.has('line_items')) {
      const next = new Set(userEditedRef.current).add('line_items');
      userEditedRef.current = next;
      setUserEdited(next);
    }
    setLineItems((previous) => previous.map((item, row) => (
      row === index ? { ...item, [field]: value } : item
    )));
    setServerComplete(false);
  };

  const addLineItem = () => {
    hasLocalEditsRef.current = true;
    if (!userEditedRef.current.has('line_items')) {
      const next = new Set(userEditedRef.current).add('line_items');
      userEditedRef.current = next;
      setUserEdited(next);
    }
    setLineItems((previous) => [...previous, { ...EMPTY_LINE_ITEM }]);
  };

  const removeLineItem = (index: number) => {
    hasLocalEditsRef.current = true;
    if (!userEditedRef.current.has('line_items')) {
      const next = new Set(userEditedRef.current).add('line_items');
      userEditedRef.current = next;
      setUserEdited(next);
    }
    setLineItems((previous) => previous.filter((_, row) => row !== index));
  };

  const handleFill = async () => {
    if (readOnlyRef.current || !flowId) return;
    setIsFilling(true);
    setError(null);
    try {
      const data = await (fillComplexServicesForm9 as unknown as (
        id: string,
        savedWork: boolean,
      ) => Promise<unknown>)(flowId, useSavedWork);
      if (data) applyServerData(data);
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setIsFilling(false);
    }
  };

  const validateFile = (file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!file.name || (extension !== 'pdf' && extension !== 'docx') || !ACCEPTED_FILE_TYPES.has(file.type)) {
      return 'Upload an AF Form 9 artifact as a PDF or DOCX file.';
    }
    if (file.size === 0) return 'The Form 9 artifact cannot be empty.';
    if (file.size > MAX_UPLOAD_BYTES) return 'The Form 9 artifact exceeds the 25 MB upload limit.';
    return null;
  };

  const acceptFile = (file: File) => {
    if (readOnlyRef.current) return;
    const validationError = validateFile(file);
    if (validationError) {
      setPendingFile(null);
      setError(validationError);
      return;
    }
    setError(null);
    setPendingFile(file);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) acceptFile(file);
  };

  const saveArtifact = async () => {
    if (readOnlyRef.current || !flowId || !pendingFile) return;
    setSaving(true);
    setError(null);
    try {
      const content_base64 = await toBase64(pendingFile);
      if (readOnlyRef.current) return;
      const result = await saveComplexServicesForm9(flowId, {
        ...buildPayload(),
        // The server owns artifact metadata and completion. This carries bytes
        // only to the stage save contract; the client never invents artifact_ref.
        artifact_upload: {
          filename: pendingFile.name,
          content_type: pendingFile.type,
          content_base64,
        },
      });
      applyServerData(result);
      lastSavedRef.current = JSON.stringify(buildPayload());
      setPendingFile(null);
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    if (readOnlyRef.current) return;
    if (!flowId || isViewOnly || !canContinue) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveComplexServicesForm9(flowId, buildPayload());
      applyServerData(result);
      if (asForm9Data(result).is_complete !== true) {
        setError('The Form 9 is not complete. Confirm all required fields and the verified artifact.');
        return;
      }
      lastSavedRef.current = JSON.stringify(buildPayload());
      onComplete(result);
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setSaving(false);
    }
  };

  const badgeFor = (fieldName: string) => {
    if (userEdited.has(fieldName)) return 'user-edited' as const;
    if (autoFilled.has(fieldName)) return 'auto-filled' as const;
    return null;
  };

  const field = (key: keyof Form9Form, label: string, options?: { multiline?: boolean; type?: string }) => (
    <ComplexField
      label={label}
      value={form[key]}
      required
      viewOnly={isViewOnly}
      multiline={options?.multiline}
      minRows={options?.multiline ? 3 : undefined}
      type={options?.type}
      InputLabelProps={options?.type === 'date' ? { shrink: true } : undefined}
      onChange={(value) => setField(key, value)}
      badge={badgeFor(key)}
    />
  );

  return (
    <Box onBlur={autoSave}>
      <SimpleStageTour ref={tourRef} tourKey="complex-services-619" steps={getSteps(!!pendingFile && !isViewOnly)} ready={!isFilling} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
        <Typography variant="h4" data-testid="complex-services-form9-title" sx={{ flex: '1 1 12rem', minWidth: 0, overflowWrap: 'anywhere' }}>
          {appendWorkflowName('AF Form 9 / Purchase Request', flowName)}
        </Typography>
        <StageTourButton onClick={() => tourRef.current?.replay()} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Record the funding request and upload the signed or approved AF Form 9 / PR. Every
        required field and the server-verified artifact are required before solicitation.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, mb: 0.5, '& .MuiFormControlLabel-root': { m: 0, minHeight: 44 } }}>
        <SavedWorkActionRow
          action={
            <Button
              variant="contained"
              data-testid="complex-services-form9-fill"
              onClick={isViewOnly ? undefined : handleFill}
              disabled={isViewOnly || isFilling}
              startIcon={isFilling ? <CircularProgress size={18} /> : undefined}
            >
              {isFilling ? 'Filling…' : 'Fill in AF Form 9'}
            </Button>
          }
          savedWork={
            <FormControlLabel
              control={<Checkbox data-testid="complex-services-form9-use-saved-work" checked={useSavedWork} onChange={isViewOnly ? undefined : (event) => setUseSavedWork(event.target.checked)} inputProps={{ readOnly: isViewOnly }} />}
              label="Use Saved Work"
            />
          }
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="complex-services-form9-error">{error}</Alert>}

      {!isSampleFlow && !canContinue && !isViewOnly && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="complex-services-form9-required-validation">
          Complete all required fields and a server-verified Form 9 artifact to continue.
        </Alert>
      )}

      {isFilling ? (
        <Box data-testid="complex-services-form9-filling" sx={{ minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Filling in AF Form 9 from saved workflow data…</Typography>
        </Box>
      ) : (
      <Box data-testid="complex-services-form9-form">
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="complex-services-form9-request">
        <Typography variant="h6" gutterBottom>Purchase Request</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>{field('purchase_request_no', 'Purchase Request No.')}</Grid>
          <Grid item xs={12} md={6}>{field('requirement_title', 'Requirement Title')}</Grid>
          <Grid item xs={12}>{field('purpose', 'Purpose', { multiline: true })}</Grid>
          <Grid item xs={12} md={6}>{field('requester_name', 'Requester Name')}</Grid>
          <Grid item xs={12} md={6}>{field('requesting_organization', 'Requesting Organization')}</Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="complex-services-form9-line-items">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Requested Line Items</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={isViewOnly}
            onClick={addLineItem}
            data-testid="complex-services-form9-add-line-item"
          >
            Add line item
          </Button>
        </Box>
        {lineItems.map((item, index) => (
          <Grid container spacing={2} sx={{ mb: 1 }} key={index}>
            <Grid item xs={12} md={1}>
              <BadgeSlot badge={badgeFor('line_items')} />
              <TextField label="Item No." size="small" fullWidth value={item.item_number} InputProps={isViewOnly ? { readOnly: true } : undefined} onChange={(event) => updateLineItem(index, 'item_number', event.target.value)} />
            </Grid>
            <Grid item xs={12} md={5}>
              <BadgeSlot badge={badgeFor('line_items')} />
              <TextField label="Description" size="small" required error={!isViewOnly && !item.description.trim()} helperText={!isViewOnly && !item.description.trim() ? 'Required' : undefined} fullWidth value={item.description} InputProps={isViewOnly ? { readOnly: true } : undefined} onChange={(event) => updateLineItem(index, 'description', event.target.value)} />
            </Grid>
            <Grid item xs={4} md={1}><BadgeSlot badge={badgeFor('line_items')} /><TextField label="Qty" size="small" fullWidth value={item.quantity} InputProps={isViewOnly ? { readOnly: true } : undefined} onChange={(event) => updateLineItem(index, 'quantity', event.target.value)} /></Grid>
            <Grid item xs={4} md={1}><BadgeSlot badge={badgeFor('line_items')} /><TextField label="Unit" size="small" fullWidth value={item.unit} InputProps={isViewOnly ? { readOnly: true } : undefined} onChange={(event) => updateLineItem(index, 'unit', event.target.value)} /></Grid>
            <Grid item xs={4} md={3}><BadgeSlot badge={badgeFor('line_items')} /><TextField label="Est. Unit Price" size="small" fullWidth value={item.estimated_unit_price} InputProps={isViewOnly ? { readOnly: true } : undefined} onChange={(event) => updateLineItem(index, 'estimated_unit_price', event.target.value)} /></Grid>
            <Grid item xs={12} md={1}><IconButton aria-label={`Remove line item ${index + 1}`} disabled={isViewOnly || lineItems.length === 1} onClick={() => removeLineItem(index)}><DeleteOutlineIcon /></IconButton></Grid>
          </Grid>
        ))}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>{field('total_amount', 'Total Amount', { type: 'number' })}</Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="complex-services-form9-funding">
        <Typography variant="h6" gutterBottom>Funding and Approval</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>{field('funding_document_number', 'Funding Document Number')}</Grid>
          <Grid item xs={12} md={6}>{field('accounting_classification', 'Accounting Classification')}</Grid>
          <Grid item xs={12} md={6}>{field('certifier_name', 'Certifier Name')}</Grid>
          <Grid item xs={12} md={6}>{field('certifier_organization', 'Certifier Organization')}</Grid>
          <Grid item xs={12} md={6}>{field('certifier_date', 'Certifier Date', { type: 'date' })}</Grid>
          <Grid item xs={12} md={6}>{field('approver_name', 'Approver Name')}</Grid>
          <Grid item xs={12} md={6}>{field('approval_date', 'Approval Date', { type: 'date' })}</Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="complex-services-form9-artifact-section">
        <Typography variant="h6" gutterBottom>AF Form 9 / PR Artifact</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload the completed purchase request separately from the funding fields above.
        </Typography>
        <input ref={fileInputRef} type="file" onClick={(event) => event.stopPropagation()} disabled={isViewOnly} accept=".pdf,.docx" onChange={handleFileInput} style={{ display: 'none' }} data-testid="complex-services-form9-file-input" />
        <Paper
          variant="outlined"
          role="button"
          tabIndex={0}
          data-testid="complex-services-form9-dropzone"
          onClick={() => !isViewOnly && openPicker(fileInputRef.current)}
          onDragEnterCapture={blockEvent} onDragOverCapture={blockEvent} onDropCapture={blockEvent}
          onDragOver={(event) => { event.preventDefault(); if (!isViewOnly) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); if (!isViewOnly && event.dataTransfer.files?.[0]) acceptFile(event.dataTransfer.files[0]); }}
          sx={{ p: 3, border: '2px dashed', cursor: isViewOnly ? 'default' : 'pointer', borderColor: dragging ? 'primary.main' : pendingFile ? 'success.main' : 'grey.400', backgroundColor: dragging ? 'action.hover' : undefined }}
          aria-label="Form 9 artifact drop zone"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadFileIcon color={pendingFile ? 'success' : 'action'} />
            <Typography>{pendingFile ? `Ready to save: ${pendingFile.name}` : 'Drag and drop a PDF or DOCX, or click to browse.'}</Typography>
          </Box>
        </Paper>
        {pendingFile && !isViewOnly && (
          <Button sx={{ mt: 2 }} variant="outlined" startIcon={saving ? <CircularProgress size={18} /> : <UploadFileIcon />} disabled={saving} onClick={saveArtifact} data-testid="complex-services-form9-save-artifact">
            {saving ? 'Saving artifact…' : 'Save Form & Upload Artifact'}
          </Button>
        )}
        {artifact ? (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="complex-services-form9-artifact">
            Server verified: <strong>{artifact.filename || 'Form 9 artifact'}</strong> ({artifact.source || 'server record'}).
            {flowId && <Button component="a" href={`${process.env.REACT_APP_API_URL || ''}/api/complex-services/${encodeURIComponent(flowId)}/form9/artifact`} size="small" startIcon={<DownloadIcon />} data-testid="complex-services-form9-download" sx={{ ml: 1 }}>Download</Button>}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="complex-services-form9-artifact-missing">
            No server-verified Form 9 artifact is available yet.
          </Alert>
        )}
      </Paper>
      </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <StageBackButton />
        <Tooltip title={isSampleFlow || canContinue ? '' : 'Complete the required purchase-request fields and save a verified artifact to continue.'}>
          <span>
            <Button variant="contained" size="large" disabled={!isSampleFlow && (isViewOnly || !canContinue)} onClick={isSampleFlow ? onSampleContinue : handleContinue} data-testid="complex-services-form9-continue">
              Continue
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ComplexServicesForm9;
